import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import { useToast } from '@/components/common/Toast';
import { locationService } from '@/services/location';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';

interface AddressFormData {
  alias: string;
  detailAddress: string;
  deliveryInstructions: string;
  additionalInfo: string;
}

export function DesktopAddressDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAddress } = location.state || {};
  
  const { addSavedAddress } = useLocationStore();
  
  const [formData, setFormData] = useState<AddressFormData>({
    alias: '',
    detailAddress: '',
    deliveryInstructions: '',
    additionalInfo: ''
  });
  
  const { showToast, ToastContainer } = useToast();
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedAddress) {
      navigate('/address-select', { replace: true });
    }
  }, [selectedAddress, navigate]);

  const handleInputChange = (field: keyof AddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAddress = async () => {
    if (!formData.alias.trim()) {
      showToast('주소의 닉네임을 입력해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      
      const fullAddress = formData.detailAddress.trim() 
        ? `${selectedAddress.roadAddress} ${formData.detailAddress.trim()}`
        : selectedAddress.roadAddress;
      
      const coordinate = await locationService.geocode(fullAddress);
      
      await addSavedAddress({
        alias: formData.alias.trim(),
        address: selectedAddress.roadAddress,
        jibunAddress: selectedAddress.jibunAddress,
        detailAddress: formData.detailAddress.trim(),
        deliveryInstructions: formData.deliveryInstructions.trim() || undefined,
        additionalInfo: formData.additionalInfo.trim() || undefined,
        coordinate: coordinate
      });

      showToast('주소가 추가되었습니다');
      
      setTimeout(() => {
        navigate('/address-select');
      }, 2000);
    } catch (error) {
      console.error('Failed to add address:', error);
      showToast('주소 추가에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!selectedAddress) {
    return null;
  }

  return (
    <DesktopLayout>
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <img src="/icons/icon_back.svg" alt="뒤로가기" width="28" height="28" />
              </button>
              <h1 className="text-2xl font-bold text-[#222222]">주소 상세 정보</h1>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              {/* Left Column - Selected Address */}
              <div>
                <h2 className="text-lg font-semibold text-[#222222] mb-4">선택된 주소</h2>
                <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                  <div className="text-lg font-medium text-gray-800 mb-2">도로명주소</div>
                  <div className="text-base text-gray-700 mb-4">{selectedAddress.roadAddress}</div>
                  
                  {selectedAddress.jibunAddress && (
                    <>
                      <div className="text-lg font-medium text-gray-800 mb-2">지번주소</div>
                      <div className="text-base text-gray-700">{selectedAddress.jibunAddress}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Right Column - Form */}
              <div>
                <h2 className="text-lg font-semibold text-[#222222] mb-4">상세 정보 입력</h2>
                <div className="space-y-6">
                  {/* Address Alias */}
                  <div>
                    <label className="block text-base font-semibold text-[#222222] mb-3">
                      주소 닉네임 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.alias}
                      onChange={(e) => handleInputChange('alias', e.target.value)}
                      onFocus={() => setFocusedField('alias')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="예: 우리집, 회사 등"
                      className={cn(
                        "w-full h-14 px-4 text-base border rounded-lg transition-colors",
                        focusedField === 'alias'
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      )}
                    />
                  </div>

                  {/* Detail Address */}
                  <div>
                    <label className="block text-base font-semibold text-[#222222] mb-3">
                      상세주소 (선택)
                    </label>
                    <input
                      type="text"
                      value={formData.detailAddress}
                      onChange={(e) => handleInputChange('detailAddress', e.target.value)}
                      onFocus={() => setFocusedField('detailAddress')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="예: 아파트 동호수, 건물명 등"
                      className={cn(
                        "w-full h-14 px-4 text-base border rounded-lg transition-colors",
                        focusedField === 'detailAddress'
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      )}
                    />
                  </div>

                  {/* Delivery Instructions */}
                  <div>
                    <label className="block text-base font-semibold text-[#222222] mb-3">
                      배송 요청사항 (선택)
                    </label>
                    <textarea
                      value={formData.deliveryInstructions}
                      onChange={(e) => handleInputChange('deliveryInstructions', e.target.value)}
                      onFocus={() => setFocusedField('deliveryInstructions')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="예: 문 앞에 놓고 가세요"
                      rows={3}
                      className={cn(
                        "w-full px-4 py-3 text-base border rounded-lg transition-colors resize-none",
                        focusedField === 'deliveryInstructions'
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      )}
                    />
                  </div>

                  {/* Additional Info */}
                  <div>
                    <label className="block text-base font-semibold text-[#222222] mb-3">
                      추가 정보 (선택)
                    </label>
                    <textarea
                      value={formData.additionalInfo}
                      onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                      onFocus={() => setFocusedField('additionalInfo')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="기타 참고사항을 입력하세요"
                      rows={3}
                      className={cn(
                        "w-full px-4 py-3 text-base border rounded-lg transition-colors resize-none",
                        focusedField === 'additionalInfo'
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="p-6 border-t border-gray-200">
            <div className="flex justify-end gap-4">
              <button
                onClick={() => navigate(-1)}
                className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold text-lg hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleAddAddress}
                disabled={isLoading || !formData.alias.trim()}
                className={cn(
                  "px-8 py-3 rounded-lg font-semibold text-lg transition-colors",
                  isLoading || !formData.alias.trim()
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                )}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>추가 중...</span>
                  </div>
                ) : (
                  '주소 추가'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopLayout>
  );
}